import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiDollarSign, FiCreditCard, FiFileText, FiArrowLeft } from 'react-icons/fi';
import { format } from 'date-fns';
import api from '../api';
import { useToast } from '../contexts/ToastContext';

export default function SubscriptionPayments() {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

  useEffect(() => {
    fetchPayments(1);
  }, []);

  const fetchPayments = async (page) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/subscriptions/payments?page=${page}&limit=10`);
      setPayments(res.data.payments);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Fetch subscription payments error:', error);
      toast.error('결제 내역을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchPayments(newPage);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>결제 내역을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn-icon" onClick={() => navigate('/subscription/manage')}>
          <FiArrowLeft />
        </button>
        <div>
          <h1>구독 결제 내역</h1>
          <p>정기구독으로 결제된 내역을 확인하세요</p>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="empty-state">
          <FiFileText size={48} color="#9CA3AF" />
          <h3>결제 내역이 없습니다</h3>
          <p>아직 정기구독 결제가 이루어지지 않았습니다.</p>
        </div>
      ) : (
        <>
          <div className="payments-list">
            {payments.map((payment) => (
              <div key={payment._id} className="payment-card">
                <div className="payment-header">
                  <div>
                    <h3>{payment.orderName}</h3>
                    <p className="payment-date">
                      <FiCalendar />
                      {format(new Date(payment.approvedAt), 'yyyy년 MM월 dd일 HH:mm')}
                    </p>
                  </div>
                  <div className="payment-amount">
                    <span className="amount">{payment.amount.toLocaleString()}원</span>
                    <span className="status-badge success">결제완료</span>
                  </div>
                </div>

                <div className="payment-details">
                  <div className="detail-row">
                    <span className="label">
                      <FiFileText />
                      주문번호
                    </span>
                    <span className="value small">{payment.orderId}</span>
                  </div>

                  {payment.method && (
                    <div className="detail-row">
                      <span className="label">
                        <FiCreditCard />
                        결제 수단
                      </span>
                      <span className="value">{payment.method}</span>
                    </div>
                  )}

                  {payment.receipt?.card && (
                    <div className="detail-row">
                      <span className="label">카드 정보</span>
                      <span className="value">
                        {payment.receipt.card.company}
                        {payment.receipt.card.number && ` (${payment.receipt.card.number})`}
                      </span>
                    </div>
                  )}

                  {payment.receipt?.url && (
                    <div className="detail-row">
                      <a
                        href={payment.receipt.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="receipt-link"
                      >
                        영수증 확인하기
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 페이지네이션 */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                이전
              </button>

              <div className="pagination-info">
                {pagination.page} / {pagination.pages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
              >
                다음
              </button>
            </div>
          )}

          {/* 요약 정보 */}
          <div className="summary-card">
            <h4>결제 요약</h4>
            <div className="summary-row">
              <span className="label">
                <FiDollarSign />
                총 결제 건수
              </span>
              <span className="value">{pagination.total}건</span>
            </div>
            <div className="summary-row">
              <span className="label">
                <FiDollarSign />
                총 결제 금액
              </span>
              <span className="value highlight">
                {payments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}원
              </span>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .page-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 32px;
        }

        .btn-icon {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-icon:hover {
          background: #F9FAFB;
          border-color: #D1D5DB;
        }

        .payments-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 32px;
        }

        .payment-card {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          overflow: hidden;
        }

        .payment-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px;
          background: linear-gradient(135deg, #F9FAFB, #FFFFFF);
          border-bottom: 1px solid #E5E7EB;
        }

        .payment-header h3 {
          margin: 0 0 8px 0;
          font-size: 16px;
          color: #111827;
        }

        .payment-date {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 0;
          font-size: 13px;
          color: #6B7280;
        }

        .payment-amount {
          text-align: right;
        }

        .payment-amount .amount {
          display: block;
          font-size: 20px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 8px;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .status-badge.success {
          background: #D1FAE5;
          color: #065F46;
        }

        .payment-details {
          padding: 20px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #F3F4F6;
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-row .label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6B7280;
          font-size: 14px;
        }

        .detail-row .value {
          font-weight: 600;
          color: #111827;
          font-size: 14px;
        }

        .detail-row .value.small {
          font-size: 12px;
          font-family: monospace;
          color: #6B7280;
        }

        .receipt-link {
          color: #4F46E5;
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
        }

        .receipt-link:hover {
          text-decoration: underline;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin: 32px 0;
        }

        .pagination-btn {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          padding: 8px 16px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 600;
          color: #374151;
        }

        .pagination-btn:hover:not(:disabled) {
          background: #F9FAFB;
          border-color: #4F46E5;
          color: #4F46E5;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          font-weight: 600;
          color: #374151;
        }

        .summary-card {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 24px;
          margin-top: 32px;
        }

        .summary-card h4 {
          margin: 0 0 16px 0;
          font-size: 16px;
          color: #111827;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #F3F4F6;
        }

        .summary-row:last-child {
          border-bottom: none;
        }

        .summary-row .label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6B7280;
          font-size: 14px;
        }

        .summary-row .value {
          font-weight: 600;
          color: #111827;
        }

        .summary-row .value.highlight {
          color: #4F46E5;
          font-size: 18px;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: #111827;
        }

        .empty-state p {
          margin: 0;
          color: #6B7280;
        }
      `}</style>
    </div>
  );
}
